import {
  collectOverdueBillingStatements,
  isBillingStatementAwaitingPayment,
  isBillingStatementOverdue,
  isParentPortalOpenStatement,
  filterParentPortalVisibleStatements,
  listParentBookingAwaitingPlayerResponseSessions,
  listParentBookingTrainerAttentionSessions,
  loadBillingStatementsForHub,
  loadParentPortalIdentifierConsolidatedStatementsRemote,
  loadHubRelevantSessions,
  fetchBankRelevanceRemote,
} from "../trainerstunden/index.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "./hub-scoped-cache.js";

function item(id, count, href, titleKey, bodyKey, actionKey, compactKey) {
  return { id, count, href, titleKey, bodyKey, actionKey, compactKey };
}

function bodyKey(base, count) {
  return count === 1 ? `${base}.body` : `${base}.body_plural`;
}

function compactKey(base, count) {
  return count === 1 ? `${base}.compact` : `${base}.compact_plural`;
}

/**
 * Leitet persönliche Hub-Aufgaben aus bereits geladenen Datensätzen ab.
 * Keine erfundenen Kennzahlen, keine plattformweiten Totale.
 */
export function collectHubAttentionItems({
  isTrainer = false,
  sessions = [],
  trainerStatements = [],
  parentStatements = [],
  bankRelevance = null,
  now = new Date(),
} = {}) {
  const items = [];

  if (isTrainer) {
    const attention = listParentBookingTrainerAttentionSessions(sessions, {
      now,
    });
    const nachruecker = [
      ...(attention.pending || []),
      ...(attention.missed || []),
    ];
    if (nachruecker.length) {
      items.push(
        item(
          "parent-booking",
          nachruecker.length,
          "/trainingsdesk/planung?filter=parent-booking",
          "hub.attention.booking.title",
          bodyKey("hub.attention.booking", nachruecker.length),
          "hub.attention.booking.action",
          compactKey("hub.attention.booking", nachruecker.length),
        ),
      );
    }

    const overdue = collectOverdueBillingStatements(trainerStatements, now);
    if (overdue.length) {
      items.push(
        item(
          "overdue-bills",
          overdue.length,
          "/trainingsdesk/abrechnung?view=unpaid",
          "hub.attention.overdue.title",
          bodyKey("hub.attention.overdue", overdue.length),
          "hub.attention.overdue.action",
          compactKey("hub.attention.overdue", overdue.length),
        ),
      );
    }

    const openNotOverdue = (
      Array.isArray(trainerStatements) ? trainerStatements : []
    ).filter(
      (st) =>
        isBillingStatementAwaitingPayment(st) &&
        !isBillingStatementOverdue(st, now),
    );
    if (openNotOverdue.length) {
      items.push(
        item(
          "open-bills",
          openNotOverdue.length,
          "/trainingsdesk/zahlungen",
          "hub.attention.openBills.title",
          bodyKey("hub.attention.openBills", openNotOverdue.length),
          "hub.attention.openBills.action",
          compactKey("hub.attention.openBills", openNotOverdue.length),
        ),
      );
    }
  }

  if (!isTrainer) {
    const awaiting = listParentBookingAwaitingPlayerResponseSessions(sessions, {
      now,
    });
    if (awaiting.length) {
      items.push(
        item(
          "rsvp",
          awaiting.length,
          "/trainingsdesk/family",
          "hub.attention.rsvp.title",
          bodyKey("hub.attention.rsvp", awaiting.length),
          "hub.attention.rsvp.action",
          compactKey("hub.attention.rsvp", awaiting.length),
        ),
      );
    }
  }

  const visible = filterParentPortalVisibleStatements(parentStatements);
  const parentOverdue = visible.filter((st) =>
    isBillingStatementOverdue(st, now),
  );
  if (parentOverdue.length) {
    items.push(
      item(
        "parent-overdue",
        parentOverdue.length,
        "/trainingsdesk/family/rechnungen",
        "hub.attention.parentOverdue.title",
        bodyKey("hub.attention.parentOverdue", parentOverdue.length),
        "hub.attention.parentOverdue.action",
        compactKey("hub.attention.parentOverdue", parentOverdue.length),
      ),
    );
  }

  const parentOpen = visible.filter(
    (st) =>
      isParentPortalOpenStatement(st) && !isBillingStatementOverdue(st, now),
  );
  if (parentOpen.length) {
    items.push(
      item(
        "parent-open",
        parentOpen.length,
        "/trainingsdesk/family/rechnungen",
        "hub.attention.parentOpen.title",
        bodyKey("hub.attention.parentOpen", parentOpen.length),
        "hub.attention.parentOpen.action",
        compactKey("hub.attention.parentOpen", parentOpen.length),
      ),
    );
  }

  if (isTrainer && bankRelevance?.checkError) {
    items.push({
      id: "tennis-bank",
      count: 0,
      href: "/trainingsdesk/zahlungen?view=abgleich",
      compactKey: "hub.attention.tennisBank.checkError",
      isError: true,
      showRefresh: true,
      lastCheckedAt: bankRelevance.lastSuccessAt || null,
    });
  } else if (isTrainer && bankRelevance?.hasConnection) {
    const s = bankRelevance.summary || {};
    const n = Number(s.count) || 0;
    items.push({
      id: "tennis-bank",
      count: n,
      href:
        n > 0
          ? "/trainingsdesk/zahlungen?view=abgleich&relevance=new"
          : "/trainingsdesk/zahlungen?view=abgleich",
      compactKey: compactKey("hub.attention.tennisBank", n),
      inCount: Number(s.inCount) || 0,
      inSum: Number(s.inSum) || 0,
      outCount: Number(s.outCount) || 0,
      outSum: Number(s.outSum) || 0,
      showRefresh: true,
      lastCheckedAt: bankRelevance.lastSuccessAt || null,
    });
  }

  return items;
}

const HUB_ATTENTION_CACHE_MS = 20000;
/** @type {Map<string, Promise<object[]>>} */
const __attentionInflight = new Map();
/** @type {Map<string, { ts: number, value: object[] }>} */
const __attentionCache = new Map();

export function clearHubAttentionItemsCache() {
  __attentionCache.clear();
  __attentionInflight.clear();
}

registerHubCacheClear(clearHubAttentionItemsCache);

function __normalizeBankRelevance(bankRes) {
  if (bankRes?.ok) {
    return {
      hasConnection: bankRes.hasConnection === true,
      summary: bankRes.summary || { count: 0 },
      lastSuccessAt: bankRes.lastSuccessAt || null,
      checkError: String(bankRes.lastError || ""),
    };
  }
  if (bankRes && bankRes.ok === false && bankRes.reason !== "unauthorized") {
    return {
      hasConnection: true,
      summary: { count: 0 },
      lastSuccessAt: null,
      checkError: bankRes.reason || "request-failed",
    };
  }
  return null;
}

function __mergeBankIntoAttentionItems(coreItems, bankRelevance) {
  const bankItems = collectHubAttentionItems({
    isTrainer: true,
    sessions: [],
    trainerStatements: [],
    parentStatements: [],
    bankRelevance,
  }).filter((row) => row?.id === "tennis-bank");
  const withoutBank = (Array.isArray(coreItems) ? coreItems : []).filter(
    (row) => row?.id !== "tennis-bank",
  );
  return [...withoutBank, ...bankItems];
}

async function __loadHubAttentionItemsImpl({
  isTrainer = false,
  onPartial = null,
  started = null,
} = {}) {
  // Bank parallel zu Core starten – nicht erst nach Core-Await.
  const bankPromise = isTrainer
    ? fetchBankRelevanceRemote().catch(() => null)
    : Promise.resolve(null);

  const [sessionsRes, trainerRes, parentRes] = await Promise.all([
    loadHubRelevantSessions().catch(() => ({ ok: false, data: [] })),
    isTrainer
      ? loadBillingStatementsForHub().catch(() => ({ ok: false, data: [] }))
      : Promise.resolve({ ok: true, data: [] }),
    loadParentPortalIdentifierConsolidatedStatementsRemote({}).catch(() => ({
      ok: false,
      statements: [],
    })),
  ]);

  if (started && !isHubReadScopeCurrent(started)) {
    return [];
  }

  const coreItems = collectHubAttentionItems({
    isTrainer,
    sessions: sessionsRes?.ok ? sessionsRes.data || [] : [],
    trainerStatements: trainerRes?.ok ? trainerRes.data || [] : [],
    parentStatements: Array.isArray(parentRes?.statements)
      ? parentRes.statements
      : [],
    bankRelevance: null,
  });

  if (typeof onPartial === "function") onPartial(coreItems);

  if (!isTrainer) return coreItems;

  const bankRelevance = __normalizeBankRelevance(await bankPromise);
  if (started && !isHubReadScopeCurrent(started)) {
    return coreItems;
  }
  if (!bankRelevance) return coreItems;

  const merged = __mergeBankIntoAttentionItems(coreItems, bankRelevance);
  if (typeof onPartial === "function") onPartial(merged);
  return merged;
}

export async function loadHubAttentionItems({
  isTrainer = false,
  onPartial = null,
} = {}) {
  const scope = await resolveHubReadScope();
  const cacheKey = `${scope.key}|${isTrainer ? "trainer" : "non-trainer"}`;
  const now = Date.now();
  const cached = __attentionCache.get(cacheKey);
  if (cached && now - cached.ts < HUB_ATTENTION_CACHE_MS) {
    if (typeof onPartial === "function") onPartial(cached.value);
    return cached.value;
  }
  const inflight = __attentionInflight.get(cacheKey);
  if (inflight) {
    if (typeof onPartial === "function") {
      inflight.then((value) => onPartial(value)).catch(() => {});
    }
    return inflight;
  }

  const started = { ...scope };
  const promise = __loadHubAttentionItemsImpl({
    isTrainer,
    onPartial,
    started,
  })
    .then((value) => {
      if (isHubReadScopeCurrent(started)) {
        __attentionCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__attentionInflight.get(cacheKey) === promise) {
        __attentionInflight.delete(cacheKey);
      }
    });

  __attentionInflight.set(cacheKey, promise);
  return promise;
}
